import React, { useCallback, useState, useEffect, useRef } from 'react'
import styles from './MultiSelect.module.css'
import cx from 'classnames'
import Checkbox from '../Checkbox'
import Loader from '../Loader'

export type SelectOption = {
    value: string
    label: string
}

type SelectProps = {
    options: SelectOption[]
    onChange: (property: string, value: string, single: SelectOption) => void
    property: string
    selected: string[]
    placeholder: string
    className: string | undefined
    loading?: boolean
}

const MultiSelect: React.FC<SelectProps> = ({
    options,
    onChange,
    property,
    placeholder,
    className,
    loading,
    selected,
}) => {
    const [isDropdownOpen, setDropdownOpen] = useState(false)
    const wrapperRef = useRef<HTMLDivElement>(null)

    const handleCheck = useCallback(
        (option: SelectOption) => {
            const data = selected.includes(option.value)
                ? selected.filter((s) => s !== option.value)
                : [...selected, option.value]

            onChange(property, data.toString(), option)
        },
        [selected, onChange, property]
    )

    const handleCheckboxChange = useCallback(
        (optionValue: string, checked: boolean) => {
            const option = options.find(opt => opt.value === optionValue)
            if (option) {
                handleCheck(option)
            }
        },
        [options, handleCheck]
    )

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setDropdownOpen(false)
            }
        }

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isDropdownOpen])

    return (
        <div ref={wrapperRef} className={cx(styles['zr-select-wrapper'], className)}>
            <input
                type="text"
                className={cx(
                    styles['zr-search-input'],
                    isDropdownOpen && styles['zr-search-active']
                )}
                value={
                    selected.length === 0 ? '' : selected.length + ' ausgewählt'
                }
                placeholder={placeholder}
                disabled={true}
                readOnly
            />
            <button
                type="button"
                className={cx(styles['zr-cover-search'])}
                onClick={() => setDropdownOpen((prev) => !prev)}
            />
            <label className={cx(selected.length > 0 && styles['zr-filled'])}>
                {placeholder}
            </label>
            <span className={cx(styles['focus-border'])}>
                <i />
            </span>
            {isDropdownOpen && options.length > 0 && (
                <div className={cx(styles['zr-dropdown'])}>
                    {options.map((option) => (
                        <Checkbox
                            key={option.value}
                            label={option.label}
                            property={option.value}
                            value={selected.includes(option.value)}
                            onChange={(prop, checked) => handleCheckboxChange(prop, checked)}
                            classes={cx(styles['zr-dropdown-item'])}
                        />
                    ))}
                    {loading && (
                        <div
                            key={'loading'}
                            className={cx(styles['zr-dropdown-item'])}
                        >
                            <Loader isLoading={loading} />
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default MultiSelect
